import { formatCredits, modeForModel } from '@hub/contracts'
import { useMemo } from 'react'
import { EllipsisIcon, Braces } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { MessageUsage } from '@hub/contracts'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ghostButton, mono } from '@/components/assistant-ui/elements/surfaces'
import { useDateLocale } from '@/i18n/dates'
import { cn } from '@/lib/utils'

interface MessageUsageMenuProps {
  usage?: MessageUsage
  /** Reply text — for the word count. */
  content: string
  createdAt?: string
  completedAt?: string
  toolCalls: number
  onToggleDetails: () => void
}

/** Raw-data toggle is a local-dev affordance only. */
const isLocalDev =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-6 px-2 py-1 text-[13px]">
      <span className="text-foreground/60">{label}</span>
      <span className={cn(mono, 'text-foreground/90 tabular-nums')}>{value}</span>
    </div>
  )
}

function wordCount(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean)
  return words.length
}

function durationSeconds(createdAt?: string, completedAt?: string): number | undefined {
  if (!createdAt || !completedAt) return undefined
  const s = (Date.parse(completedAt) - Date.parse(createdAt)) / 1000
  if (!Number.isFinite(s) || s < 0) return undefined
  return s
}

/**
 * The "…" action of a reply: usage stats for the turn. Shows whatever the
 * provider reported (tokens, cache, cost) — the basis for the credits view
 * later — plus simple derived numbers (words, tool calls, duration).
 */
export function MessageUsageMenu({
  usage,
  content,
  createdAt,
  completedAt,
  toolCalls,
  onToggleDetails,
}: MessageUsageMenuProps) {
  const { t } = useTranslation('chat')
  const { intl } = useDateLocale()
  const fmt = useMemo(() => new Intl.NumberFormat(intl), [intl])
  const words = wordCount(content)
  const seconds = durationSeconds(createdAt, completedAt)
  const took =
    seconds === undefined
      ? undefined
      : seconds < 60
        ? t('usage.durationSeconds', { seconds: seconds.toFixed(1) })
        : t('usage.durationMinutes', {
            minutes: Math.floor(seconds / 60),
            seconds: Math.round(seconds % 60),
          })
  const cached = usage ? usage.cacheReadTokens + usage.cacheWriteTokens : 0

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={t('usage.more')} className={cn(ghostButton, 'size-7')}>
          <EllipsisIcon className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-foreground/50 text-xs font-medium uppercase tracking-wide">
          {t('usage.title')}
        </DropdownMenuLabel>
        {usage ? (
          <>
            {modeForModel(usage.model) && (
              <Row label={t('usage.mode')} value={t(`mode.${modeForModel(usage.model)!}`)} />
            )}
            <Row label={t('usage.tokensIn')} value={fmt.format(usage.inputTokens + cached)} />
            <Row label={t('usage.tokensOut')} value={fmt.format(usage.outputTokens)} />
            {usage.reasoningTokens > 0 && (
              <Row label={t('usage.reasoning')} value={fmt.format(usage.reasoningTokens)} />
            )}
            {cached > 0 && <Row label={t('usage.cache')} value={fmt.format(cached)} />}
            <Row label={t('usage.total')} value={fmt.format(usage.totalTokens)} />
            {usage.modelCalls > 1 && (
              <Row label={t('usage.modelCalls')} value={String(usage.modelCalls)} />
            )}
            {usage.credits && (
              <>
                <Row label={t('usage.credits')} value={formatCredits(usage.credits.total, intl)} />
                {/* Mixed rates (tokens + flat tool charges) — a sum, not a product. */}
                {usage.credits.toolCalls > 0 && (
                  <p className="text-foreground/45 px-2 pb-1 text-[11px]">
                    {t('usage.creditsBreakdown', {
                      llm: formatCredits(usage.credits.llm, intl),
                      tools: formatCredits(usage.credits.tools, intl),
                    })}
                  </p>
                )}
              </>
            )}
          </>
        ) : (
          <p className="text-foreground/50 px-2 py-1 text-[13px]">{t('usage.none')}</p>
        )}
        <DropdownMenuSeparator />
        <Row label={t('usage.words')} value={fmt.format(words)} />
        {toolCalls > 0 && <Row label={t('usage.toolCalls')} value={String(toolCalls)} />}
        {took && <Row label={t('usage.duration')} value={took} />}
        {isLocalDev && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onToggleDetails}
              className="text-foreground/50 gap-1.5 text-[11px]"
            >
              <Braces className="size-3" />
              {t('usage.showRaw')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
